import {
  Caption1,
  Checkbox,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Field,
  Input,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  OverlayDrawer,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Textarea,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowLeft16Regular,
  ArrowUpRight16Regular,
  Attach16Regular,
  Calendar12Regular,
  Calendar16Regular,
  ClipboardTextLtr16Regular,
  Delete16Regular,
  ShieldCheckmark16Regular,
  Document16Regular,
  Dismiss24Regular,
  Link16Regular,
  MoreHorizontal20Regular,
  Note16Regular,
  Person16Regular,
  Tag16Regular,
  TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { FuiButton, FuiFilterSelect, FuiInput } from "@/components/fui-control";
import { usePortalContainer } from "@/components/ui/use-portal-container";
import type { ExecutionPriority, ExecutionStatus, ExecutionTask } from "@/engine/types";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { useCan, useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { formatDate } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { canCompleteExecutionTask, checklistProgress, describeGateBlock, knownAssignees } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";
import { recordCode, recordsForExecutionTask } from "@/store/record-ops";
import { RecordChip } from "@/features/records/record-ui";
import { TaskActivityFeed, TaskActivityLog } from "./TaskActivityFeed";
import {
  buildDevTaskFeedMock,
  isDevFeedMockId,
  mergeDevFeedMock,
  stripDevFeedMockAttachments,
} from "./dev-task-feed-mock";
import { AttachmentPanel } from "./AttachmentPanel";
import { GateChecklistSection } from "./GateChecklistSection";
import { useShakeStyles } from "./GateTagChip";
import { LinkedDocumentRows, useTombstoneSentence } from "@/features/docs/LinkedDocuments";
import { DetailSection as Section, DetailTabs, type DetailTab } from "./detail-parts";
import { openScopeInSchedule } from "./links";
import { TaskCommandMenu, taskMenuTarget } from "./TaskCommandMenu";
import { DateCalendar, DatePresets } from "@/components/date-field";
import { AssigneePicker, DateInput, LabelPicker, ScopePicker } from "./pickers";
import { LabelChip, PRIORITY_VALUE_OPTIONS, ScopeChip, STATUS_VALUE_OPTIONS, TaskPriorityChip, TaskStatusChip, useTaskContext } from "./task-ui";
import { scopeRefs } from "./task-query";

const useStyles = makeStyles({
  drawer: {
    width: "520px",
    maxWidth: "100vw",
  },
  backdrop: {
    backdropFilter: "blur(2.5px)",
    WebkitBackdropFilter: "blur(2.5px)",
    backgroundColor: "rgba(0, 0, 0, 0.16)",
  },
  header: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  backRow: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalXXS, minWidth: 0, marginLeft: `calc(-1 * ${tokens.spacingHorizontalS})` },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "32px",
    width: "100%",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    marginLeft: "auto",
  },
  headerTitleRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  titleInput: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase500,
    width: "100%",
    flexGrow: 1,
    "& input": {
      width: "100%",
    },
  },
  titleRead: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase500,
    width: "100%",
    padding: "6px 0",
    color: tokens.colorNeutralForeground1,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalXXL,
  },
  bodyActivity: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    overflowY: "hidden",
    height: "100%",
    minHeight: 0,
    gap: 0,
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    width: "100%",
    padding: "2px 4px",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":hover .checkActions": {
      display: "flex",
    },
    "& .checkActions:focus-within": {
      display: "flex",
    },
  },
  checkCheckbox: {
    minWidth: "auto",
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    flexShrink: 0,
    marginRight: "2px",
    "& .fui-Checkbox__indicator": {
      width: "14px",
      height: "14px",
      minWidth: "14px",
      minHeight: "14px",
      ...shorthands.margin(0),
      fontSize: "9px",
    },
    "& .fui-Checkbox__indicator svg": {
      width: "10px",
      height: "10px",
    },
  },
  checkText: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    "& input": {
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
  },
  checkActions: {
    display: "none",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
    marginLeft: "auto",
  },
  addRow: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    alignItems: "center",
    width: "100%",
  },
  miniChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    padding: "1px 6px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  done: {
    textDecoration: "line-through",
    color: tokens.colorNeutralForeground3,
    "& input": {
      textDecoration: "line-through",
      color: tokens.colorNeutralForeground3,
    },
  },
  drawerFooter: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
    gap: tokens.spacingHorizontalS,
  },
  footerActions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginLeft: "auto",
  },
});

/** Detail editor for the execution task in `executionFocusId`. Mounted on project layout and 내 작업. */
export function ExecutionTaskDrawer() {
  const id = useProjectStore((s) => s.executionFocusId);
  const focusTab = useProjectStore((s) => s.executionFocusTab);
  const focusCommentId = useProjectStore((s) => s.executionFocusCommentId);
  const task = useProjectStore((s) => (s.executionFocusId ? s.project.executionTasks.find((t) => t.id === s.executionFocusId) : undefined));
  const [continuousAdd, setContinuousAdd] = useState(false);
  const portalContainer = usePortalContainer();
  const portalClassName = portalContainer?.id === "dialog-layer" ? portalContainer.className : "";
  const close = () => {
    const s = useProjectStore.getState();
    if (s.executionFocusIsNew && s.executionFocusId) {
      s.deleteExecutionTasks([s.executionFocusId]);
    }
    s.setExecutionFocus(null);
    setContinuousAdd(false);
  };
  const styles = useStyles();

  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id]);

  if (!task || !id || !portalClassName) return null;
  return (
    <OverlayDrawer
      open
      mountNode={{ className: portalClassName }}
      position="end"
      modalType="alert"
      className={styles.drawer}
      backdrop={{
        className: styles.backdrop,
        onMouseDown: (e) => e.preventDefault(),
      }}
      onOpenChange={(e, d) => {
        e.preventDefault();
        if (d.type === "backdropClick") return;
        if (!d.open) close();
      }}
      aria-label="Task 상세"
    >
      <DrawerContent
        key={`${id}:${focusTab}:${focusCommentId ?? ""}`}
        task={task}
        onClose={close}
        continuousAdd={continuousAdd}
        onContinuousAddChange={setContinuousAdd}
        initialTab={focusTab}
        focusCommentId={focusCommentId}
      />
    </OverlayDrawer>
  );
}

function DrawerContent({
  task,
  onClose,
  continuousAdd,
  onContinuousAddChange,
  initialTab,
  focusCommentId,
}: {
  task: ExecutionTask;
  onClose: () => void;
  continuousAdd: boolean;
  onContinuousAddChange: (val: boolean) => void;
  initialTab: DetailTab;
  focusCommentId: string | null;
}) {
  const styles = useStyles();
  const surface = usePlanSurface();
  const own = useOwnWork();
  const canComment = useCan("plan.comment.write");
  const documentNotice = useTombstoneSentence("executionTask", [task.id]);
  const ctx = useTaskContext();
  const project = useProjectStore((s) => s.project);
  const projectId = useProjectStore((s) => s.activeId) ?? "";
  const isNew = useProjectStore((s) => s.executionFocusIsNew);
  const canWrite = surface.editTask && (isNew ? own.canCreateTask : own.executionOwn(task.assignees));
  const returnRecord = useProjectStore((s) => (s.executionReturnRecordId ? s.project.records.find((r) => r.id === s.executionReturnRecordId) : undefined));
  const s = useProjectStore.getState;
  const globalResources = useCatalogStore((state) => state.resources);
  const suggestions = useMemo(() => {
    const fromTasks = knownAssignees(project);
    const fromAssignments = (project.resourceAssignments || [])
      .map((a) => globalResources[a.resourceId]?.displayName)
      .filter((n): n is string => Boolean(n));
    return Array.from(new Set([...fromAssignments, ...fromTasks]));
  }, [project, globalResources]);
  const [draft, setDraft] = useState<ExecutionTask>(() => ({ ...task }));
  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab);
  const [devFeedMockOn, setDevFeedMockOn] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const isDev = process.env.NODE_ENV !== "production";

  const devFeedMock = useMemo(() => (isDev ? buildDevTaskFeedMock(task.id) : null), [isDev, task.id]);
  const displayTask = useMemo(
    () => (devFeedMockOn && devFeedMock ? mergeDevFeedMock(task, devFeedMock) : task),
    [devFeedMockOn, devFeedMock, task],
  );
  const displayDraft = useMemo(
    () =>
      devFeedMockOn && devFeedMock
        ? { ...draft, attachments: [...draft.attachments, ...devFeedMock.attachments] }
        : draft,
    [devFeedMockOn, devFeedMock, draft],
  );

  const activeCommentsCount = useMemo(() => displayTask.comments.filter((c) => !c.deleted).length, [displayTask.comments]);
  const attachmentsCount = displayDraft.attachments.length;

  useEffect(() => {
    setDevFeedMockOn(false);
  }, [task.id]);

  useEffect(() => {
    if (isNew) {
      const t = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [task.id, isNew]);

  const shake = useShakeStyles();
  const [lockMessage, setLockMessage] = useState<string | null>(null);
  const [shaking, setShaking] = useState(false);
  const gates = task.gateTags ?? [];

  // gate decisions are written straight to the store and may move the status: keep the draft in step
  useEffect(() => {
    setDraft((prev) => (prev.status === task.status ? prev : { ...prev, status: task.status }));
  }, [task.status]);

  /** Hard Lock: "done" is refused (with a shake and a hint) while any gate is not passed. */
  const requestStatus = (status: ExecutionStatus | undefined) => {
    if (status === "done" && task.status !== "done") {
      const { allowed, blockingGates } = canCompleteExecutionTask(task);
      if (!allowed) {
        setLockMessage(describeGateBlock(blockingGates));
        setShaking(true);
        window.setTimeout(() => setShaking(false), 400);
        return;
      }
    }
    setLockMessage(null);
    updateDraft({ status });
  };

  const updateDraft = (patch: Partial<ExecutionTask>) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      if (patch.attachments) {
        next.attachments = stripDevFeedMockAttachments(patch.attachments);
      }
      if (next.coverAttachmentId && isDevFeedMockId(next.coverAttachmentId)) {
        next.coverAttachmentId = prev.coverAttachmentId;
      }
      return next;
    });
  };

  const handleCancelCreate = () => {
    s().deleteExecutionTasks([task.id]);
    s().setExecutionFocus(null);
    onContinuousAddChange(false);
  };

  const handleSave = () => {
    const trimmed = draft.title.trim();
    const finalTitle = trimmed || (isNew ? "새 Task" : "");

    const patch: Partial<ExecutionTask> = {};
    if (finalTitle !== task.title) patch.title = finalTitle;
    if (draft.status !== task.status) patch.status = draft.status;
    if (draft.priority !== task.priority) patch.priority = draft.priority;
    if (draft.bucketId !== task.bucketId) patch.bucketId = draft.bucketId;
    if (draft.assignees.join("|") !== task.assignees.join("|")) patch.assignees = draft.assignees;
    if (draft.start !== task.start) patch.start = draft.start;
    if (draft.due !== task.due) patch.due = draft.due;
    if ((draft.notes || undefined) !== (task.notes || undefined)) patch.notes = draft.notes;
    if (draft.linkedTaskIds.join("|") !== task.linkedTaskIds.join("|")) patch.linkedTaskIds = draft.linkedTaskIds;
    if (draft.labelIds.join("|") !== task.labelIds.join("|")) patch.labelIds = draft.labelIds;
    if (draft.showChecklistOnCard !== task.showChecklistOnCard) patch.showChecklistOnCard = draft.showChecklistOnCard;
    if (draft.coverAttachmentId !== task.coverAttachmentId) patch.coverAttachmentId = draft.coverAttachmentId;
    if (JSON.stringify(draft.checklist) !== JSON.stringify(task.checklist)) patch.checklist = draft.checklist;
    if (JSON.stringify(draft.attachments) !== JSON.stringify(task.attachments)) patch.attachments = draft.attachments;

    if (Object.keys(patch).length > 0) {
      s().updateExecutionTask(task.id, patch);
    }

    if (isNew && continuousAdd) {
      // opened from a record: keep linking the next Task to that record (and keep the way back)
      if (returnRecord) s().createTaskForRecord(returnRecord.id);
      else s().addExecutionTask({ bucketId: draft.bucketId }, { open: true });
    } else {
      s().setExecutionFocus(null);
    }
  };

  const refs = scopeRefs(draft, ctx);
  const linkedRecords = recordsForExecutionTask(project.records, task.id);

  return (
    <>
      <DrawerHeader className={styles.header}>
        <div className={styles.headerTop}>
          {returnRecord ? (
            <div className={styles.backRow}>
              <FuiButton
                appearance="subtle"
                density="dialog"
                icon={<ArrowLeft16Regular />}
                aria-label={`${recordCode(returnRecord)} ${returnRecord.title}(으)로 돌아가기`}
                title={returnRecord.title}
                onClick={isNew ? handleCancelCreate : onClose}
              >
                {recordCode(returnRecord)}
              </FuiButton>
              <Caption1 className={styles.muted}>· {isNew ? "새 대응 Task" : "Task 상세"}</Caption1>
            </div>
          ) : (
            <Caption1 className={styles.muted}>{isNew ? "새 Task 생성" : "Task 상세"}</Caption1>
          )}
          <div className={styles.headerActions}>
            {!isNew && (canWrite || refs.length > 0) && (
              <TaskCommandMenu
                task={taskMenuTarget(draft)}
                canWrite={canWrite}
                assignees={suggestions}
                onStatus={(status) => requestStatus(status)}
                onPriority={(priority) => updateDraft({ priority })}
                onAssignees={(assignees) => updateDraft({ assignees })}
                onDuplicate={() => s().duplicateExecutionTask(task.id)}
                onDelete={() => {
                  s().deleteExecutionTasks([task.id]);
                  s().setExecutionFocus(null);
                }}
                deleteNotice={documentNotice}
                extra={
                  <>
                    {refs.length > 0 && <MenuItem onClick={() => openScopeInSchedule(projectId, refs[0]!.id)}>일정에서 연결 범위 보기</MenuItem>}
                    {isDev && canWrite && (
                      <MenuItem onClick={() => setDevFeedMockOn((on) => !on)}>
                        {devFeedMockOn ? "원래 상태로 돌리기" : "댓글·첨부 목업 더 보이기"}
                      </MenuItem>
                    )}
                  </>
                }
                trigger={<FuiButton appearance="subtle" icon={<MoreHorizontal20Regular />} aria-label="더 보기" />}
              />
            )}
            <FuiButton appearance="subtle" icon={<Dismiss24Regular />} aria-label="닫기" onClick={isNew ? handleCancelCreate : onClose} />
          </div>
        </div>
        <div className={styles.headerTitleRow}>
          {canWrite ? (
            <Input
              ref={titleInputRef}
              autoFocus={isNew}
              appearance="underline"
              className={styles.titleInput}
              style={{ width: "100%" }}
              value={draft.title}
              placeholder={isNew ? "새 Task 제목 입력" : "Task 제목"}
              aria-label="제목"
              onChange={(e) => updateDraft({ title: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  if (isNew && continuousAdd) {
                    e.preventDefault();
                    handleSave();
                  } else {
                    (e.target as HTMLInputElement).blur();
                  }
                }
              }}
            />
          ) : (
            <div className={styles.titleRead}>{draft.title || "제목 없음"}</div>
          )}
        </div>

        {!isNew && (
          <DetailTabs
            value={activeTab}
            onChange={setActiveTab}
            counts={{ comments: activeCommentsCount, attachments: attachmentsCount, activity: displayTask.activity.length }}
            ariaLabel="Task 상세 탭"
          />
        )}
      </DrawerHeader>

      <DrawerBody className={mergeClasses(styles.body, !isNew && activeTab !== "details" && styles.bodyActivity)}>
        {isNew || activeTab === "details" ? (
          <>
            {canWrite ? (
            <>
            <div className={styles.grid3}>
              <FuiFilterSelect
                label="상태"
                value={draft.status ?? ""}
                options={STATUS_VALUE_OPTIONS}
                onValueChange={(v) => requestStatus((v || undefined) as ExecutionStatus | undefined)}
                style={{ width: "100%" }}
                className={shaking ? shake.shake : undefined}
              />
              <FuiFilterSelect
                label="우선순위"
                value={draft.priority ?? ""}
                options={PRIORITY_VALUE_OPTIONS}
                onValueChange={(v) => updateDraft({ priority: (v || undefined) as ExecutionPriority | undefined })}
                style={{ width: "100%" }}
              />
              <FuiFilterSelect
                label="버킷"
                value={draft.bucketId ?? ""}
                options={[{ value: "", label: "없음" }, ...project.planner.buckets.map((b) => ({ value: b.id, label: b.name }))]}
                onValueChange={(v) => updateDraft({ bucketId: v || undefined })}
                style={{ width: "100%" }}
              />
            </div>

            {lockMessage && (
              <Caption1 role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>
                {lockMessage}
              </Caption1>
            )}

            <Field label="담당자" size="small">
              <AssigneePicker value={draft.assignees} onChange={(assignees) => updateDraft({ assignees })} suggestions={suggestions} />
            </Field>

            <div className={styles.grid2}>
              <Field label="시작" size="small">
                <DateInput value={draft.start} max={draft.due} onChange={(start) => updateDraft({ start })} ariaLabel="시작일" />
              </Field>
              <Field label="마감" size="small">
                <DateInput value={draft.due} min={draft.start} onChange={(due) => updateDraft({ due })} ariaLabel="마감일" />
              </Field>
            </div>

            <Section icon={<Note16Regular />} title="설명">
              <NotesEditor value={draft.notes ?? ""} onCommit={(notes) => updateDraft({ notes: notes || undefined })} />
            </Section>

            {!isNew && (
              <Section icon={<Document16Regular />} title="문서">
                <LinkedDocumentRows kind="executionTask" targetId={task.id} canEdit={canWrite} />
              </Section>
            )}

            <Section icon={<Link16Regular />} title="연결된 WBS · 작업" hint={refs.length ? `${refs.length}개` : undefined}>
              <ScopePicker value={draft.linkedTaskIds} onChange={(linkedTaskIds) => updateDraft({ linkedTaskIds })} ctx={ctx} />
              {refs.length > 0 && (
                <div className={styles.chips}>
                  {refs.map((r) => (
                    <ScopeChip key={r.id} ref={r} onOpen={(id) => openScopeInSchedule(projectId, id)} />
                  ))}
                </div>
              )}
            </Section>

            {!isNew && linkedRecords.length > 0 && (
              <Section icon={<ClipboardTextLtr16Regular />} title="관련 이슈·결정" hint={`${linkedRecords.length}개`}>
                <div className={styles.chips}>
                  {linkedRecords.map((r) => (
                    <RecordChip
                      key={r.id}
                      record={r}
                      onOpen={(id) => {
                        if (canWrite) handleSave();
                        s().setRecordFocus(id);
                      }}
                    />
                  ))}
                </div>
              </Section>
            )}

            <Section
              icon={<Tag16Regular />}
              title="라벨"
              action={
                <LabelPicker
                  value={draft.labelIds}
                  onChange={(labelIds) => updateDraft({ labelIds })}
                  labels={project.planner.labels}
                  trigger={<FuiButton appearance="subtle" density="dialog" icon={<Add16Regular />}>라벨</FuiButton>}
                />
              }
            >
              <div className={styles.chips}>
                {draft.labelIds.length === 0 && <Caption1 className={styles.muted}>라벨 없음</Caption1>}
                {project.planner.labels
                  .filter((l) => draft.labelIds.includes(l.id))
                  .map((l) => (
                    <LabelChip key={l.id} label={l} dismissible onClick={() => updateDraft({ labelIds: draft.labelIds.filter((x) => x !== l.id) })} />
                  ))}
              </div>
            </Section>

            <Section
              icon={<TaskListSquareLtr16Regular />}
              title="체크리스트"
              hint={
                draft.checklist.length
                  ? `${checklistProgress(draft).done}/${draft.checklist.length} 완료`
                  : undefined
              }
              action={
                draft.checklist.length > 0 ? (
                  <Tooltip content="보드 카드에 체크리스트 노출" relationship="label">
                    <Checkbox
                      checked={draft.showChecklistOnCard !== false}
                      onChange={(_, d) => updateDraft({ showChecklistOnCard: !!d.checked })}
                      label={<Caption1 style={{ color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200 }}>카드에 표시</Caption1>}
                    />
                  </Tooltip>
                ) : undefined
              }
            >
              <Checklist draft={draft} updateDraft={updateDraft} suggestions={suggestions} readOnly={!canWrite} />
            </Section>

            {!isNew && (gates.length > 0 || canWrite) && (
              <Section
                icon={<ShieldCheckmark16Regular />}
                title="검수"
                hint={gates.length ? `${gates.filter((g) => g.status === "passed").length}/${gates.length} 통과` : undefined}
              >
                <GateChecklistSection taskId={task.id} gates={gates} />
              </Section>
            )}

            {/* 신규 생성 모드에서는 첨부파일을 폼 하단에 직접 제공 */}
            {isNew && (
              <Section
                icon={<Attach16Regular />}
                title="첨부 파일 및 링크"
                hint={draft.attachments.length ? `${draft.attachments.length}개` : undefined}
                collapsible
                defaultOpen={false}
              >
                <AttachmentPanel
                  value={draft.attachments}
                  onChange={(attachments) => updateDraft({ attachments })}
                  coverId={draft.coverAttachmentId}
                  onCoverChange={(coverAttachmentId) => updateDraft({ coverAttachmentId })}
                />
              </Section>
            )}
            </>
            ) : (
            <>
            <div className={styles.grid3}>
              <Field label="상태" size="small">
                <TaskStatusChip status={draft.status} />
              </Field>
              <Field label="우선순위" size="small">
                <TaskPriorityChip priority={draft.priority} />
              </Field>
              <Field label="버킷" size="small">
                <Caption1>{project.planner.buckets.find((b) => b.id === draft.bucketId)?.name ?? "—"}</Caption1>
              </Field>
            </div>
            <Field label="담당자" size="small">
              <Caption1>{draft.assignees.join(", ") || "—"}</Caption1>
            </Field>
            <div className={styles.grid2}>
              <Field label="시작" size="small">
                <Caption1>{draft.start ? formatDate(draft.start, "full") : "—"}</Caption1>
              </Field>
              <Field label="마감" size="small">
                <Caption1>{draft.due ? formatDate(draft.due, "full") : "—"}</Caption1>
              </Field>
            </div>
            <Section icon={<Note16Regular />} title="설명">
              <Caption1 style={{ whiteSpace: "pre-wrap" }}>{draft.notes || "—"}</Caption1>
            </Section>
            <Section icon={<Document16Regular />} title="문서">
              <LinkedDocumentRows kind="executionTask" targetId={task.id} />
            </Section>
            {refs.length > 0 && (
              <Section icon={<Link16Regular />} title="연결된 WBS · 작업" hint={`${refs.length}개`}>
                <div className={styles.chips}>
                  {refs.map((r) => (
                    <ScopeChip key={r.id} ref={r} onOpen={(id) => openScopeInSchedule(projectId, id)} />
                  ))}
                </div>
              </Section>
            )}
            {linkedRecords.length > 0 && (
              <Section icon={<ClipboardTextLtr16Regular />} title="관련 이슈·결정" hint={`${linkedRecords.length}개`}>
                <div className={styles.chips}>
                  {linkedRecords.map((r) => (
                    <RecordChip key={r.id} record={r} onOpen={(id) => s().setRecordFocus(id)} />
                  ))}
                </div>
              </Section>
            )}
            <Section icon={<Tag16Regular />} title="라벨">
              <div className={styles.chips}>
                {draft.labelIds.length === 0 && <Caption1 className={styles.muted}>라벨 없음</Caption1>}
                {project.planner.labels
                  .filter((l) => draft.labelIds.includes(l.id))
                  .map((l) => (
                    <LabelChip key={l.id} label={l} />
                  ))}
              </div>
            </Section>
            <Section
              icon={<TaskListSquareLtr16Regular />}
              title="체크리스트"
              hint={draft.checklist.length ? `${checklistProgress(draft).done}/${draft.checklist.length} 완료` : undefined}
            >
              <Checklist draft={draft} updateDraft={updateDraft} suggestions={suggestions} readOnly />
            </Section>
            </>
            )}

            {!isNew && (
              <Caption1 className={styles.muted}>
                생성 {formatDate(task.createdAt, "full")} · 수정 {formatDate(task.updatedAt, "dateTime")}
                {task.completedAt ? ` · 완료 ${formatDate(task.completedAt, "dateTime")}` : ""}
              </Caption1>
            )}
          </>
        ) : activeTab === "comments" ? (
          <TaskActivityFeed task={displayTask} taskId={task.id} focusCommentId={focusCommentId} readOnly={!canComment} />
        ) : activeTab === "activity" ? (
          <TaskActivityLog items={displayTask.activity} />
        ) : activeTab === "attachments" ? (
          <AttachmentPanel
            value={displayDraft.attachments}
            onChange={(attachments) => updateDraft({ attachments })}
            coverId={displayDraft.coverAttachmentId}
            onCoverChange={(coverAttachmentId) => updateDraft({ coverAttachmentId })}
            readOnly={!canWrite}
          />
        ) : null}
      </DrawerBody>

      {canWrite && (isNew || activeTab === "details") && (
        <DrawerFooter className={styles.drawerFooter}>
          {isNew ? (
            <Checkbox
              checked={continuousAdd}
              onChange={(_, d) => onContinuousAddChange(!!d.checked)}
              label={<Caption1 style={{ color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, userSelect: "none" }}>연속 추가</Caption1>}
            />
          ) : null}
          <div className={styles.footerActions}>
            <FuiButton appearance="secondary" onClick={isNew ? handleCancelCreate : onClose}>
              취소
            </FuiButton>
            <FuiButton appearance="primary" onClick={handleSave}>
              저장
            </FuiButton>
          </div>
        </DrawerFooter>
      )}
    </>
  );
}

function ChecklistAssigneeMenu({
  current,
  options,
  onSelect,
  trigger,
}: {
  current?: string;
  options: string[];
  onSelect: (name: string | undefined) => void;
  trigger?: ReactElement;
}) {
  const allOptions = useMemo(() => Array.from(new Set(options.filter(Boolean))), [options]);
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        {trigger ?? (
          <Tooltip content="담당자 지정" relationship="label">
            <FuiButton
              appearance="subtle"
              density="dialog"
              icon={<Person16Regular />}
              aria-label="담당자 지정"
            />
          </Tooltip>
        )}
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {current && (
            <>
              <MenuItem onClick={() => onSelect(undefined)}>담당자 해제 ({current})</MenuItem>
              <MenuDivider />
            </>
          )}
          {allOptions.length === 0 && <MenuItem disabled>등록된 담당자 없음</MenuItem>}
          {allOptions.map((name) => (
            <MenuItem key={name} icon={<PersonAvatar name={name} size={20} tip={false} />} onClick={() => onSelect(name)}>
              {name}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function ChecklistDatePopover({
  current,
  onSelect,
  trigger,
}: {
  current?: string;
  onSelect: (due: string | undefined) => void;
  trigger?: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      trapFocus
      onOpenChange={(e, d) => {
        if (!d.open && e.target instanceof Element && e.target.closest(".fui-DatePicker__popupSurface")) return;
        setOpen(d.open);
      }}
    >
      <PopoverTrigger disableButtonEnhancement>
        {trigger ?? (
          <Tooltip content="마감일 설정" relationship="label">
            <FuiButton
              appearance="subtle"
              density="dialog"
              icon={<Calendar16Regular />}
              aria-label="마감일 설정"
            />
          </Tooltip>
        )}
      </PopoverTrigger>
      <PopoverSurface style={{ padding: tokens.spacingVerticalS, display: "flex", flexDirection: "column", gap: 6, minWidth: "180px" }}>
        <Caption1>서브태스크 마감일</Caption1>
        <DatePresets
          value={current}
          onSelect={(due) => {
            onSelect(due);
            setOpen(false);
          }}
        />
        <DateCalendar
          value={current}
          onSelect={(due) => {
            onSelect(due);
            setOpen(false);
          }}
        />
      </PopoverSurface>
    </Popover>
  );
}

function Checklist({
  draft,
  updateDraft,
  suggestions,
  readOnly = false,
}: {
  draft: ExecutionTask;
  updateDraft: (patch: Partial<ExecutionTask>) => void;
  suggestions: string[];
  readOnly?: boolean;
}) {
  const styles = useStyles();
  const s = useProjectStore.getState;
  const [text, setText] = useState("");

  const add = () => {
    if (!text.trim()) return;
    const newItem = {
      id: `chk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      text: text.trim(),
      done: false,
    };
    updateDraft({ checklist: [...draft.checklist, newItem] });
    setText("");
  };

  const updateItem = (id: string, patch: Partial<(typeof draft.checklist)[number]>) => {
    updateDraft({
      checklist: draft.checklist.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  const removeItem = (id: string) => {
    updateDraft({
      checklist: draft.checklist.filter((c) => c.id !== id),
    });
  };

  const convertItemToTask = (id: string) => {
    const c = draft.checklist.find((x) => x.id === id);
    if (!c) return;
    s().addExecutionTask(
      {
        title: c.text,
        assignees: c.assignee ? [c.assignee] : [],
        due: c.due,
        linkedTaskIds: draft.linkedTaskIds,
        bucketId: draft.bucketId,
      },
      { open: false },
    );
    removeItem(id);
  };

  if (readOnly) {
    if (draft.checklist.length === 0) return <Caption1 className={styles.muted}>항목 없음</Caption1>;
    return (
      <>
        {draft.checklist.map((c) => (
          <div key={c.id} className={styles.checkRow}>
            <span className={mergeClasses(styles.checkText, c.done && styles.done)}>
              {c.done ? "완료" : "열림"} · {c.text}
              {c.assignee ? ` · ${c.assignee}` : ""}
              {c.due ? ` · ${formatDate(c.due, "short")}` : ""}
            </span>
          </div>
        ))}
      </>
    );
  }

  const renderItem = (c: (typeof draft.checklist)[number]) => (
    <div key={c.id} className={styles.checkRow}>
      <Checkbox
        className={styles.checkCheckbox}
        shape="circular"
        checked={c.done}
        onChange={(_, d) => updateItem(c.id, { done: !!d.checked })}
        aria-label={c.text}
      />
      <InlineText
        className={mergeClasses(styles.checkText, c.done && styles.done)}
        value={c.text}
        onCommit={(v) => v.trim() && v !== c.text && updateItem(c.id, { text: v.trim() })}
      />
      {c.assignee && (
        <ChecklistAssigneeMenu
          current={c.assignee}
          options={[...draft.assignees, ...suggestions]}
          onSelect={(assignee) => updateItem(c.id, { assignee })}
          trigger={
            <Tooltip content={`담당: ${c.assignee} (클릭하여 변경)`} relationship="label">
              <span className={styles.miniChip} role="button" tabIndex={0}>
                <PersonAvatar name={c.assignee} size={16} tip={false} />
                <Caption1 style={{ fontSize: tokens.fontSizeBase100 }}>{c.assignee}</Caption1>
              </span>
            </Tooltip>
          }
        />
      )}
      {c.due && (
        <ChecklistDatePopover
          current={c.due}
          onSelect={(due) => updateItem(c.id, { due })}
          trigger={
            <Tooltip content={`마감: ${formatDate(c.due, "full")} (클릭하여 변경)`} relationship="label">
              <span className={styles.miniChip} role="button" tabIndex={0}>
                <Calendar12Regular />
                <Caption1 style={{ fontSize: tokens.fontSizeBase100 }}>{formatDate(c.due, "short")}</Caption1>
              </span>
            </Tooltip>
          }
        />
      )}
      <div className={mergeClasses(styles.checkActions, "checkActions")}>
        {!c.assignee && (
          <ChecklistAssigneeMenu
            options={[...draft.assignees, ...suggestions]}
            onSelect={(assignee) => updateItem(c.id, { assignee })}
          />
        )}
        {!c.due && (
          <ChecklistDatePopover
            onSelect={(due) => updateItem(c.id, { due })}
          />
        )}
        <Tooltip content="독립 Task로 승격" relationship="label">
          <FuiButton
            appearance="subtle"
            density="dialog"
            icon={<ArrowUpRight16Regular />}
            aria-label="독립 Task로 승격"
            onClick={() => convertItemToTask(c.id)}
          />
        </Tooltip>
        <Tooltip content="항목 삭제" relationship="label">
          <FuiButton
            appearance="subtle"
            density="dialog"
            icon={<Delete16Regular />}
            aria-label="항목 삭제"
            onClick={() => removeItem(c.id)}
          />
        </Tooltip>
      </div>
    </div>
  );

  return (
    <>
      {draft.checklist.map(renderItem)}

      <div className={styles.addRow}>
        <FuiInput
          style={{ width: "100%" }}
          placeholder="항목 추가 후 ↩"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          aria-label="체크리스트 항목 추가"
        />
      </div>
    </>
  );
}

function InlineText({ value, onCommit, className }: { value: string; onCommit: (v: string) => void; className?: string }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <Input
      appearance="filled-lighter"
      size="small"
      className={className}
      value={v}
      title={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          setV(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function NotesEditor({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <Textarea
      resize="vertical"
      rows={3}
      value={v}
      placeholder="태스크에 대한 상세 설명이나 배경을 입력하세요..."
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== value && onCommit(v)}
      aria-label="설명"
    />
  );
}
